import React, { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import Layout from '../components/Layout';
import PageLoader from '../components/PageLoader';
import API from '../api/axios';
import {
  FiPlus, FiX, FiEdit2, FiTrash2, FiChevronDown,
  FiChevronRight, FiDownload, FiCheck, FiSearch,
} from 'react-icons/fi';
import './MaterialPlanning.css';

/* ── Searchable Project Dropdown ── */
function ProjectSearchSelect({ projects, value, onChange, plannedIds }) {
  const [open, setOpen]         = useState(false);
  const [search, setSearch]     = useState('');
  const ref                     = useRef(null);
  const selected                = projects.find(p => p._id === value);

  useEffect(() => {
    function handler(e) { if (ref.current && !ref.current.contains(e.target)) setOpen(false); }
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const filtered = projects.filter(p =>
    `${p.projectId || ''} ${p.title} ${p.client?.name || ''}`.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="mp-proj-search-wrap" ref={ref}>
      <div className="mp-proj-search-trigger" onClick={() => { setOpen(o => !o); setSearch(''); }}>
        <span className="mp-proj-search-val">
          {selected ? `${selected.projectId ? selected.projectId + ' – ' : ''}${selected.title}` : 'Select Project'}
        </span>
        <FiChevronDown size={13} className={`mp-proj-search-icon ${open ? 'open' : ''}`} />
      </div>
      {open && (
        <div className="mp-proj-dropdown">
          <div className="mp-proj-search-input-wrap">
            <FiSearch size={13} className="mp-proj-search-ico" />
            <input
              autoFocus
              className="mp-proj-search-input"
              placeholder="Search project..."
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
          </div>
          <div className="mp-proj-options">
            {filtered.length === 0 && <div className="mp-proj-no-results">No projects found</div>}
            {filtered.map(p => {
              const hasPlan = plannedIds?.has(String(p._id));
              return (
                <div
                  key={p._id}
                  className={`mp-proj-option ${p._id === value ? 'selected' : ''}`}
                  onClick={() => { onChange(p); setOpen(false); }}
                >
                  {p.projectId && <span className="mp-proj-opt-id">{p.projectId}</span>}
                  <span className="mp-proj-opt-title">{p.title}</span>
                  {p.client?.name && <span className="mp-proj-opt-client">{p.client.name}</span>}
                  <span className={`mp-proj-opt-plan-badge ${hasPlan ? 'mp-plan-yes' : 'mp-plan-no'}`}>
                    {hasPlan ? 'Plan Created' : 'No Plan'}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

const STATUS_CFG = {
  planned:     { label: 'Planned',     color: '#2563eb', bg: '#eff6ff', border: '#bfdbfe' },
  in_progress: { label: 'In Progress', color: '#d97706', bg: '#fffbeb', border: '#fde68a' },
  completed:   { label: 'Completed',   color: '#16a34a', bg: '#f0fdf4', border: '#bbf7d0' },
  not_started: { label: 'Not Started', color: '#64748b', bg: '#f8fafc', border: '#e2e8f0' },
};

function fmtINR(n) { if (!n && n !== 0) return '—'; return '₹' + Number(n).toLocaleString('en-IN'); }
function fmt(iso) { if (!iso) return '—'; return new Date(iso).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }); }

/* ── Confirm Dialog ── */
function ConfirmDialog({ title, message, confirmLabel = 'Confirm', confirmColor = '#dc2626', icon, onConfirm, onCancel }) {
  return (
    <div className="mp-confirm-overlay" onClick={onCancel}>
      <div className="mp-confirm-box" onClick={e => e.stopPropagation()}>
        {icon && <div className="mp-confirm-icon">{icon}</div>}
        <h4 className="mp-confirm-title">{title}</h4>
        <p className="mp-confirm-msg">{message}</p>
        <div className="mp-confirm-actions">
          <button className="mp-confirm-cancel" onClick={onCancel}>Cancel</button>
          <button className="mp-confirm-ok" style={{ background: confirmColor }} onClick={onConfirm}>{confirmLabel}</button>
        </div>
      </div>
    </div>
  );
}

export default function MaterialPlanning() {
  const location = useLocation();
  const [projects, setProjects]               = useState([]);
  const [plannedIds, setPlannedIds]           = useState(new Set());
  const [selectedProject, setSelectedProject] = useState(null);
  const [plan, setPlan]                       = useState(null);
  const [activeCatId, setActiveCatId]         = useState(null);
  const [showCatModal, setShowCatModal]       = useState(false);
  const [editCat, setEditCat]                 = useState(null);
  const [showMatModal, setShowMatModal]       = useState(false);
  const [editMat, setEditMat]                 = useState(null);
  const [selectedMatId, setSelectedMatId]     = useState(null);
  const [confirm, setConfirm]                 = useState(null);
  const [loading, setLoading]                 = useState(true);


  function askConfirm(opts) {
    return new Promise(resolve => {
      setConfirm({
        ...opts,
        onConfirm: () => { setConfirm(null); resolve(true); },
        onCancel:  () => { setConfirm(null); resolve(false); },
      });
    });
  }

  useEffect(() => {
    const pid = new URLSearchParams(location.search).get('projectId');
    Promise.all([
      API.get('/projects'),
      API.get('/material-planning').catch(() => ({ data: [] })),
    ])
      .then(([projRes, planRes]) => {
        const ids = new Set((planRes.data || []).map(id => String(id)));
        setPlannedIds(ids);
        setProjects(projRes.data);
        const target = pid ? projRes.data.find(p => p._id === pid) : null;
        setSelectedProject(target || projRes.data[0] || null);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { if (selectedProject) { loadPlan(); } }, [selectedProject]); // eslint-disable-line react-hooks/exhaustive-deps

  function loadPlan() {
    API.get(`/material-planning/${selectedProject._id}`)
      .then(r => {
        setPlan(r.data);
        setActiveCatId(prev => {
          if (prev && r.data?.categories?.find(c => c._id === prev)) return prev;
          return r.data?.categories?.[0]?._id || null;
        });
      })
      .catch(() => setPlan(null));
  }

  // Active category
  const activeCat = plan?.categories?.find(c => c._id === activeCatId) || null;

  // All materials of the active category
  const tabMaterials = activeCat?.materials || [];

  // Summary stats — recalc from categories for accuracy
  const totalCategories = plan?.categories?.length || 0;
  const allMaterials = (cats) => (cats || []).flatMap(c => c.materials || []);
  const totalMaterials  = allMaterials(plan?.categories).length;
  const totalQty        = allMaterials(plan?.categories).reduce((s, m) => s + (+m.requiredQty || 0), 0);
  const estimatedCost   = (plan?.categories || []).reduce((s, c) => s + (+c.categoryTotal || 0), 0);

  // Donut
  const statusCounts = {
    planned:     (plan?.categories || []).filter(c => c.status === 'planned').length,
    in_progress: (plan?.categories || []).filter(c => c.status === 'in_progress').length,
    completed:   (plan?.categories || []).filter(c => c.status === 'completed').length,
  };
  const pct = totalCategories ? Math.round((statusCounts.completed / totalCategories) * 100) : 0;

  // Consolidated
  const consolidated = {};
  allMaterials(plan?.categories).forEach(m => {
    const key = m.materialName;
    if (!consolidated[key]) consolidated[key] = { materialName: key, unit: m.unit, totalQty: 0, totalAmount: 0 };
    consolidated[key].totalQty    += +m.requiredQty;
    consolidated[key].totalAmount += +m.amount;
  });
  const consolidatedList = Object.values(consolidated);

  async function deleteCategory(catId) {
    const ok = await askConfirm({ title: 'Delete Category', message: 'Delete this category and all its materials? This cannot be undone.', confirmLabel: 'Delete', icon: '🗑️' });
    if (!ok) return;
    try {
      const r = await API.delete(`/material-planning/${selectedProject._id}/categories/${catId}`);
      setPlan(r.data);
      if (activeCatId === catId) setActiveCatId(r.data.categories?.[0]?._id || null);
      toast.success('Category deleted');
    } catch { toast.error('Failed to delete category'); }
  }

  async function deleteMaterial(catId, matId) {
    const ok = await askConfirm({ title: 'Delete Material', message: 'Remove this material from the category?', confirmLabel: 'Delete', icon: '🗑️' });
    if (!ok) return;
    try {
      const r = await API.delete(`/material-planning/${selectedProject._id}/categories/${catId}/materials/${matId}`);
      setPlan(r.data);
      setSelectedMatId(null);
      toast.success('Material deleted');
    } catch { toast.error('Failed to delete material'); }
  }

  async function deleteSelectedMaterial() {
    if (!selectedMatId || !activeCat) {
      toast.info('Select a material row first to delete');
      return;
    }
    await deleteMaterial(activeCat._id, selectedMatId);
  }

  async function initPlan() {
    if (!selectedProject) return toast.error('Select a project first');
    try {
      const r = await API.post(`/material-planning/${selectedProject._id}`, { categories: [] });
      setPlannedIds(prev => new Set(prev).add(String(selectedProject._id)));
      setPlan(r.data);
      toast.success('Material plan initialized');
      setShowCatModal(true);
    } catch { toast.error('Failed to create plan'); }
  }

  function exportPDF() {
    if (!selectedProject) return;
    const logoUrl = `${window.location.origin}/cmpy_logo.png`;
    const catRows = (plan?.categories || []).map((cat, ci) => {
      const matRows = (cat.materials || []).map((m, mi) => `
        <tr>
          <td>${ci + 1}.${mi + 1}</td>
          <td>${m.materialName}</td>
          <td>${m.heightFt ?? '—'}</td>
          <td>${m.widthFt ?? '—'}</td>
          <td>${(m.heightFt && m.widthFt) ? (m.heightFt * m.widthFt * (m.qty || 1)).toLocaleString('en-IN') : '—'}</td>
          <td>${m.requiredQty}</td>
          <td>₹${Number(m.rate).toLocaleString('en-IN')}</td>
          <td>₹${Number(m.amount).toLocaleString('en-IN')}</td>
          <td>${m.remarks || '—'}</td>
        </tr>`).join('');
      const st = STATUS_CFG[cat.status] || STATUS_CFG.not_started;
      return `
        <tr class="cat-head">
          <td>${ci + 1}</td>
          <td colspan="6"><b>${cat.icon || '📦'} ${cat.title}</b>
            <span style="margin-left:8px;padding:1px 8px;border-radius:999px;font-size:10px;font-weight:600;background:${st.bg};color:${st.color}">${st.label}</span>
          </td>
          <td><b>₹${Number(cat.categoryTotal).toLocaleString('en-IN')}</b></td>
          <td></td>
        </tr>
        ${matRows}`;
    }).join('');

    const consRows = consolidatedList.map(m => `
      <tr>
        <td>${m.materialName}</td>
        <td>${m.totalQty}</td>
        <td>₹${Number(m.totalAmount).toLocaleString('en-IN')}</td>
      </tr>`).join('');

    const win = window.open('', '_blank');
    win.document.write(`<!DOCTYPE html><html><head><title>Material Plan — ${selectedProject.title}</title><style>
      body{font-family:Arial,sans-serif;padding:24px;color:#222;font-size:12px}
      .header{display:flex;align-items:center;justify-content:space-between;border-bottom:2px solid #2563eb;padding-bottom:12px;margin-bottom:16px}
      .header img{height:48px;width:auto;object-fit:contain}
      .meta{display:flex;flex-wrap:wrap;gap:14px;padding:10px 14px;background:#f8fafc;border-radius:8px;margin-bottom:14px;border:1px solid #e2e8f0}
      .meta-cell{display:flex;flex-direction:column;gap:2px}
      .meta-cell span{font-size:10px;color:#94a3b8;text-transform:uppercase}
      .meta-cell strong{font-size:12px}
      table{width:100%;border-collapse:collapse;margin:10px 0}
      th{background:#2563eb;color:#fff;padding:7px 10px;text-align:left;font-size:10px}
      td{padding:5px 10px;border-bottom:1px solid #f1f5f9;font-size:11px}
      .cat-head td{background:#eff6ff;font-weight:600;border-top:1px solid #bfdbfe}
      .subcat-head td{background:#f8fafc;font-weight:600;font-size:10px;color:#475569}
      .summary{display:grid;grid-template-columns:repeat(4,1fr);gap:10px;margin:14px 0}
      .sum-card{padding:10px 14px;background:#f8fafc;border-radius:8px;border:1px solid #e2e8f0;text-align:center}
      .sum-card .val{font-size:16px;font-weight:800;color:#0f172a}
      .sum-card .lbl{font-size:10px;color:#94a3b8;margin-top:2px}
      .sum-card.green .val{color:#16a34a}
      h3{font-size:12px;font-weight:700;color:#0f172a;margin:16px 0 6px;text-transform:uppercase;letter-spacing:.5px}
      .footer{margin-top:20px;padding-top:10px;border-top:1px solid #e2e8f0;display:flex;justify-content:space-between;font-size:10px;color:#94a3b8}
      @media print{button{display:none}}
    </style></head><body>
      <div class="header">
        <div style="display:flex;align-items:center;gap:12px">
          <img src="${logoUrl}" alt="Logo" onerror="this.style.display='none'" />
          <div><div style="font-size:17px;font-weight:700">Interior Design Co.</div><div style="font-size:11px;color:#64748b">Material Planning Report</div></div>
        </div>
        <div style="text-align:right"><div style="font-size:18px;font-weight:800;color:#2563eb">MATERIAL PLAN</div><div style="font-size:11px;color:#64748b">${selectedProject.title}</div></div>
      </div>
      <div class="meta">
        <div class="meta-cell"><span>Project</span><strong>${selectedProject.title}</strong></div>
        <div class="meta-cell"><span>Customer</span><strong>${selectedProject.client?.name || '—'}</strong></div>
        <div class="meta-cell"><span>Start Date</span><strong>${fmt(selectedProject.startDate)}</strong></div>
        <div class="meta-cell"><span>Completion</span><strong>${fmt(selectedProject.endDate)}</strong></div>
        <div class="meta-cell"><span>Project Value</span><strong>₹${Number(selectedProject.budget || 0).toLocaleString('en-IN')}</strong></div>
      </div>
      <div class="summary">
        <div class="sum-card"><div class="val">${totalCategories}</div><div class="lbl">Categories</div></div>
        <div class="sum-card"><div class="val">${totalMaterials}</div><div class="lbl">Materials</div></div>
        <div class="sum-card"><div class="val">${totalQty}</div><div class="lbl">Total Qty</div></div>
        <div class="sum-card green"><div class="val">₹${Number(estimatedCost).toLocaleString('en-IN')}</div><div class="lbl">Estimated Cost</div></div>
      </div>
      <h3>Category-wise Material Details</h3>
      <table>
        <thead><tr><th>#</th><th>Material Name</th><th>Height (ft)</th><th>Width (ft)</th><th>Total Area (SQ/FT)</th><th>Qty</th><th>Rate</th><th>Amount</th><th>Remarks</th></tr></thead>
        <tbody>${catRows}</tbody>
      </table>
      ${consolidatedList.length > 0 ? `
      <h3>Consolidated Material Requirement</h3>
      <table>
        <thead><tr><th>Material Name</th><th>Total Qty</th><th>Total Amount</th></tr></thead>
        <tbody>${consRows}
          <tr style="background:#f0fdf4"><td colspan="2"><b>Grand Total</b></td><td><b>₹${Number(estimatedCost).toLocaleString('en-IN')}</b></td></tr>
        </tbody>
      </table>` : ''}
      <div class="footer">
        <span>Generated on ${fmt(new Date().toISOString())}</span>
        <span>Completion: ${pct}% (${statusCounts.completed}/${totalCategories} categories)</span>
      </div>
      <script>window.onload=()=>{window.print();}${'<'}/script>
    </body></html>`);
    win.document.close();
  }

  const projLabel = selectedProject
    ? `${selectedProject.projectId ? selectedProject.projectId + ' – ' : ''}${selectedProject.title}`
    : 'Select Project';

  return (
    <Layout>
      {loading && <PageLoader text="Loading material planning..." />}
      <div className="mp-root">

        {/* ── Topbar ── */}
        <div className="mp-topbar">
          <div className="mp-proj-select-wrap">
            <label>Select Project</label>
            <ProjectSearchSelect
              projects={projects}
              value={selectedProject?._id || ''}
              plannedIds={plannedIds}
              onChange={p => {
                if (p?._id === selectedProject?._id) return;
                setSelectedProject(p);
                setActiveCatId(null);
                setPlan(null);
              }}
            />
          </div>
          <div className="mp-topbar-actions">
            <button className="mp-btn-primary" onClick={() => plan ? setShowCatModal(true) : initPlan()}>
              <FiPlus size={13} /> New Material Plan
            </button>
          </div>
        </div>

        {/* ── Project Info Card ── */}
        {selectedProject && (
          <div className="mp-proj-card">
            <div className="mp-proj-img"><span>🏠</span></div>
            <div className="mp-proj-details">
              <div className="mp-proj-name-row">
                <h2 className="mp-proj-name">{projLabel}</h2>
                <span className="mp-proj-status-chip">
                  {plan ? 'Material Planning Started' : 'Plan Not Created'}
                </span>
              </div>
              <div className="mp-proj-meta">
                <div className="mp-meta-item"><span>👤</span><div><p>Customer</p><strong>{selectedProject.client?.name || '—'}</strong></div></div>
                <div className="mp-meta-sep" />
                <div className="mp-meta-item"><span>🏗️</span><div><p>Project Type</p><strong>{selectedProject.style || 'Interior'}</strong></div></div>
                <div className="mp-meta-sep" />
                <div className="mp-meta-item"><span>💰</span><div><p>Project Value</p><strong>{fmtINR(selectedProject.budget)}</strong></div></div>
                <div className="mp-meta-sep" />
                <div className="mp-meta-item"><span>📅</span><div><p>Start Date</p><strong>{fmt(selectedProject.startDate)}</strong></div></div>
                <div className="mp-meta-sep" />
                <div className="mp-meta-item"><span>📅</span><div><p>Expected Completion</p><strong>{fmt(selectedProject.endDate)}</strong></div></div>
                <div className="mp-meta-sep" />
                <div className="mp-meta-item"><span>👷</span><div><p>Supervisor</p><strong>{selectedProject.designer?.name || '—'}</strong></div></div>
              </div>
            </div>
          </div>
        )}

        {/* ── Main grid ── */}
        <div className="mp-main-grid">
          {/* LEFT */}
          <div className="mp-left-col">
            <div className="mp-card">
              <div className="mp-card-header">
                <h3>Work / Category List</h3>
                <button className="mp-add-cat-btn" onClick={() => { setEditCat(null); setShowCatModal(true); }}>
                  <FiPlus size={12} /> Add Category
                </button>
              </div>
              <div className="mp-cat-list">
                {/* Added categories */}
                {(plan?.categories || [])
                  .map((cat, i) => (
                  <div key={cat._id}
                    className={`mp-cat-row ${activeCatId === cat._id ? 'mp-cat-active' : ''}`}
                    onClick={() => { setActiveCatId(cat._id); setSelectedMatId(null); }}
                  >
                    <div className="mp-cat-icon-wrap">{cat.icon || '📦'}</div>
                    <div className="mp-cat-info">
                      <div className="mp-cat-name-row">
                        <span className="mp-cat-num">{i + 1}</span>
                        <span className="mp-cat-name">{cat.title}</span>
                      </div>
                      <div className="mp-cat-meta-row">
                        <span className="mp-cat-total">{fmtINR(cat.categoryTotal)}</span>
                      </div>
                    </div>
                    <div className="mp-cat-right" onClick={e => e.stopPropagation()}>
                      <div className="mp-cat-actions">
                        <button className="mp-icon-btn" onClick={() => { setEditCat(cat); setShowCatModal(true); }}><FiEdit2 size={11}/></button>
                        <button className="mp-icon-btn mp-del" onClick={() => deleteCategory(cat._id)}><FiTrash2 size={11}/></button>
                      </div>
                      <button
                        className="mp-cat-arrow-btn"
                        onClick={() => { setActiveCatId(cat._id); setSelectedMatId(null); }}
                        title="View materials"
                      >
                        <FiChevronRight size={14} />
                      </button>
                    </div>
                  </div>
                ))}

                {!plan?.categories?.length && (
                  <p className="mp-empty">No categories yet. Click "+ Add Category" to start.</p>
                )}
              </div>
            </div>

            {/* Summary */}
            <div className="mp-card">
              <h3 className="mp-card-h3">Material Planning Summary</h3>
              <div className="mp-summary-rows">
                <div className="mp-sum-row"><span>Total Categories</span><strong>{totalCategories}</strong></div>
                <div className="mp-sum-row"><span>Total Materials</span><strong>{totalMaterials}</strong></div>
                <div className="mp-sum-row"><span>Total Quantity (All Units)</span><strong>{totalQty.toLocaleString('en-IN')}</strong></div>
                <div className="mp-sum-row mp-sum-highlight"><span>Estimated Cost</span><strong>{fmtINR(estimatedCost)}</strong></div>
              </div>
            </div>
          </div>

          {/* RIGHT */}
          <div className="mp-right-col">
            <div className="mp-card">
              <div className="mp-mat-header">
                <div className="mp-mat-title-row">
                  <span className="mp-mat-icon">{activeCat?.icon || '📦'}</span>
                  <h3>{activeCat ? activeCat.title : 'Select a category'}</h3>
                </div>
              </div>

              <div className="mp-mat-actions">
                <button className="mp-btn-sm mp-btn-outline"
                  onClick={() => { if (!activeCat) { toast.info('Select or add a category first'); return; } setEditMat(null); setShowMatModal(true); }}>
                  <FiPlus size={11} /> Add Material
                </button>
                <button className="mp-btn-sm mp-btn-danger"
                  onClick={deleteSelectedMaterial}>
                  <FiTrash2 size={11} /> Delete Material
                  {selectedMatId && <span className="mp-sel-dot" />}
                </button>
              </div>

              <div className="mp-table-wrap">
              <table className="mp-table">
                <thead>
                  <tr><th>#</th><th>Material Name</th><th>Height (ft)</th><th>Width (ft)</th><th>Total Area (SQ/FT)</th><th>Required Qty</th><th>Rate (₹)</th><th>Amount (₹)</th><th>Remarks</th><th>Action</th></tr>
                </thead>
                <tbody>
                  {!activeCat && <tr><td colSpan={10} className="mp-empty">Select a category to view materials</td></tr>}
                  {activeCat && tabMaterials.length === 0 && <tr><td colSpan={10} className="mp-empty">No materials yet. Click "+ Add Material".</td></tr>}
                  {tabMaterials.map((m, i) => (
                    <tr key={m._id}
                      className={`mp-mat-row ${selectedMatId === m._id ? 'mp-mat-selected' : ''}`}
                      onClick={() => setSelectedMatId(prev => prev === m._id ? null : m._id)}>
                      <td className="mp-td-num">{i + 1}</td>
                      <td className="mp-td-name">{m.materialName}</td>
                      <td>{m.heightFt ?? '–'}</td>
                      <td>{m.widthFt ?? '–'}</td>
                      <td>{(m.heightFt && m.widthFt) ? (m.heightFt * m.widthFt * (m.qty || 1)).toLocaleString('en-IN') : '–'}</td>
                      <td>{m.requiredQty}</td>
                      <td>{fmtINR(m.rate)}</td>
                      <td>{fmtINR(m.amount)}</td>
                      <td className="mp-td-remark">{m.remarks || '–'}</td>
                      <td onClick={e => e.stopPropagation()}>
                        <div className="mp-row-acts">
                          <button className="mp-icon-btn" onClick={() => { setEditMat(m); setShowMatModal(true); }}><FiEdit2 size={12}/></button>
                          <button className="mp-icon-btn mp-del" onClick={() => deleteMaterial(activeCat._id, m._id)}><FiTrash2 size={12}/></button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {activeCat && tabMaterials.length > 0 && (
                    <tr className="mp-cat-total-row">
                      <td colSpan={7}><strong>Category Total</strong></td>
                      <td className="mp-cat-total-amt">{fmtINR(activeCat.categoryTotal)}</td>
                      <td /><td />
                    </tr>
                  )}
                </tbody>
              </table>
              </div>
            </div>


            {/* Consolidated */}
            {consolidatedList.length > 0 && (
              <div className="mp-card">
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '.65rem' }}>
                  <h3 className="mp-consolidated-title">Consolidated Material Requirement</h3>
                  <button className="mp-btn-sm mp-btn-outline" onClick={exportPDF}><FiDownload size={11}/> Export PDF</button>
                </div>
                <div className="mp-table-wrap">
                <table className="mp-table mp-consolidated-table">
                  <thead>
                    <tr><th>#</th><th>Material Name</th><th>Total Qty</th><th>Total Amount (₹)</th></tr>
                  </thead>
                  <tbody>
                    {consolidatedList.map((m, i) => (
                      <tr key={i}>
                        <td className="mp-td-num">{i + 1}</td>
                        <td>{m.materialName}</td>
                        <td>{m.totalQty}</td>
                        <td>{fmtINR(m.totalAmount)}</td>
                      </tr>
                    ))}
                    <tr className="mp-grand-total-row">
                      <td colSpan={3}><strong>Grand Total (All Categories)</strong></td>
                      <td className="mp-grand-amt">{fmtINR(estimatedCost)}</td>
                    </tr>
                  </tbody>
                </table>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {confirm && <ConfirmDialog {...confirm} />}

      {showCatModal && (
        <CategoryModal
          cat={editCat}
          projectId={selectedProject?._id}
          selectedProject={selectedProject}
          onClose={() => { setShowCatModal(false); setEditCat(null); }}
          onSaved={p => {
            setPlan(p);
            if (!activeCatId && p.categories?.length) setActiveCatId(p.categories[0]._id);
            setShowCatModal(false); setEditCat(null);
            toast.success(editCat ? 'Category updated' : 'Category added');
          }}
        />
      )}

      {showMatModal && activeCat && (
        <MaterialModal
          mat={editMat}
          categoryId={activeCat._id}
          projectId={selectedProject?._id}
          onClose={() => { setShowMatModal(false); setEditMat(null); }}
          onSaved={p => {
            setPlan(p); setShowMatModal(false); setEditMat(null);
            toast.success(editMat ? 'Material updated' : 'Material added');
          }}
        />
      )}
    </Layout>
  );
}

/* ── Category Modal ── */
function CategoryModal({ cat, projectId, onClose, onSaved }) {
  const [form, setForm]     = useState({ title: cat?.title || '', status: cat?.status || 'not_started' });
  const [saving, setSaving] = useState(false);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  async function save() {
    if (!form.title.trim()) return toast.error('Category name is required');
    setSaving(true);
    try {
      if (cat) {
        // Edit: simple update
        const r = await API.put(`/material-planning/${projectId}/categories`, { categoryId: cat._id, ...form });
        onSaved(r.data);
      } else {
        const r = await API.put(`/material-planning/${projectId}/categories`, form);
        onSaved(r.data);
      }
    } catch { toast.error('Failed to save category'); }
    finally { setSaving(false); }
  }

  return (
    <div className="mp-overlay">
      <div className="mp-modal mp-modal-sm" onClick={e => e.stopPropagation()}>
        <div className="mp-modal-header">
          <h3>{cat ? 'Edit Category' : 'Add Category'}</h3>
          <button className="mp-modal-close" onClick={onClose}><FiX /></button>
        </div>
        <div className="mp-modal-body">

          <div className="mp-mgroup">
            <label>Category Name *</label>
            <input
              value={form.title}
              onChange={e => set('title', e.target.value)}
              placeholder="e.g. Kitchen, Bedroom, Flooring…"
              autoFocus={!!cat}
            />
          </div>
        </div>
        <div className="mp-modal-footer">
          <button className="mp-btn-ghost" onClick={onClose}>Cancel</button>
          <button className="mp-btn-primary" onClick={save} disabled={saving}>
            {saving ? 'Saving…' : cat ? 'Update' : 'Add Category'}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── Material Modal ── */
function MaterialModal({ mat, categoryId, projectId, onClose, onSaved }) {
  const [form, setForm] = useState({
    materialName:  mat?.materialName  || '',
    heightFt:      mat?.heightFt      ?? '',
    widthFt:       mat?.widthFt       ?? '',
    qty:           mat?.qty           ?? 1,
    unit:          mat?.unit          || 'Nos',
    requiredQty:   mat?.requiredQty   || '',
    rate:          mat?.rate          || '',
    remarks:       mat?.remarks       || '',
  });
  const [saving, setSaving] = useState(false);
  const NUMERIC_FIELDS = ['heightFt', 'widthFt', 'qty', 'requiredQty', 'rate'];
  const set = (k, v) => {
    if (NUMERIC_FIELDS.includes(k) && v !== '' && +v < 0) v = '0';
    setForm(f => {
      const next = { ...f, [k]: v };
      if (k === 'heightFt' || k === 'widthFt' || k === 'qty') {
        const area = (+next.heightFt || 0) * (+next.widthFt || 0) * (+next.qty || 1);
        next.requiredQty = area > 0 ? area : '';
      }
      return next;
    });
  };
  const preview = (+(form.requiredQty) || 0) * (+(form.rate) || 0);
  const areaPreview = (+(form.heightFt) || 0) * (+(form.widthFt) || 0) * (+(form.qty) || 1);
  const hasDims = form.heightFt !== '' || form.widthFt !== '';

  async function save() {
    if (!form.materialName.trim()) return toast.error('Material name is required');
    if (NUMERIC_FIELDS.some(k => +(form[k]) < 0)) return toast.error('Negative values are not allowed');
    if (hasDims && (!form.heightFt || !form.widthFt)) return toast.error('Enter both Height and Width to auto-calculate Qty');
    if (!hasDims && (!form.requiredQty || +form.requiredQty <= 0)) return toast.error('Required Qty is required');
    setSaving(true);
    try {
      const payload = mat ? { materialId: mat._id, ...form } : form;
      const r = await API.put(`/material-planning/${projectId}/categories/${categoryId}/materials`, payload);
      onSaved(r.data);
    } catch { toast.error('Failed to save material'); }
    finally { setSaving(false); }
  }

  return (
    <div className="mp-overlay">
      <div className="mp-modal" onClick={e => e.stopPropagation()}>
        <div className="mp-modal-header">
          <h3>{mat ? 'Edit Material' : 'Add Material'}</h3>
          <button className="mp-modal-close" onClick={onClose}><FiX /></button>
        </div>
        <div className="mp-modal-body">
          <div className="mp-mrow">
            <div className="mp-mgroup mp-mfull">
              <label>Material Name *</label>
              <input value={form.materialName} onChange={e => set('materialName', e.target.value)} placeholder="e.g. Plywood (BWP)" autoFocus />
            </div>
          </div>
          <div className="mp-mrow">
            <div className="mp-mgroup">
              <label>Height (ft)</label>
              <input type="number" min="0" step="0.01" value={form.heightFt} onChange={e => set('heightFt', e.target.value)} placeholder="0" />
            </div>
            <div className="mp-mgroup">
              <label>Width (ft)</label>
              <input type="number" min="0" step="0.01" value={form.widthFt} onChange={e => set('widthFt', e.target.value)} placeholder="0" />
            </div>
          </div>
          {hasDims && (
            <div className="mp-mrow">
              <div className="mp-mgroup">
                <label>Qty</label>
                <input type="number" min="0" step="1" value={form.qty} onChange={e => set('qty', e.target.value)} placeholder="1" />
              </div>
            </div>
          )}
          {areaPreview > 0 && (
            <div className="mp-amount-preview">
              <FiCheck size={13} color="#16a34a" /> Total Area: <strong>{areaPreview.toLocaleString('en-IN')} SQ/FT</strong>
            </div>
          )}
          <div className="mp-mrow">
            <div className="mp-mgroup">
              <label>Required Qty {hasDims && <span style={{ color: '#94a3b8', fontWeight: 400 }}>(auto: H × W × Qty)</span>}</label>
              <input
                type="number" min="0"
                value={form.requiredQty}
                onChange={e => set('requiredQty', e.target.value)}
                placeholder="0"
                readOnly={hasDims}
                disabled={hasDims}
                style={hasDims ? { background: '#f8fafc', color: '#64748b', cursor: 'not-allowed' } : undefined}
              />
            </div>
            <div className="mp-mgroup">
              <label>Rate (₹)</label>
              <input type="number" min="0" value={form.rate} onChange={e => set('rate', e.target.value)} placeholder="0" />
            </div>
          </div>
          {preview > 0 && (
            <div className="mp-amount-preview">
              <FiCheck size={13} color="#16a34a" /> Amount: <strong>₹{preview.toLocaleString('en-IN')}</strong>
            </div>
          )}
          <div className="mp-mgroup">
            <label>Remarks</label>
            <input value={form.remarks} onChange={e => set('remarks', e.target.value)} placeholder="Optional note" />
          </div>
        </div>
        <div className="mp-modal-footer">
          <button className="mp-btn-ghost" onClick={onClose}>Cancel</button>
          <button className="mp-btn-primary" onClick={save} disabled={saving}>{saving ? 'Saving…' : mat ? 'Update Material' : 'Add Material'}</button>
        </div>
      </div>
    </div>
  );
}
